import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { awardXP } from '../utils/xp';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { loadGamePool, pickGameWords } from '../data/gameWords';
import './TranslationSprint.css';

const TEXT = {
    title: { en: 'Translation sprint', it: 'Sprint di traduzione', es: 'Sprint de traducción', fr: 'Sprint de traduction', pt: 'Sprint de tradução' },
    loading: { en: 'Loading words...', it: 'Caricamento parole...', es: 'Cargando palabras...', fr: 'Chargement des mots...', pt: 'Carregando palavras...' },
    notEnough: {
        en: 'Not enough words yet. Add some to your Word Bank!',
        it: 'Non ci sono ancora abbastanza parole. Aggiungine qualcuna al tuo Word Bank!',
        es: 'Aún no hay suficientes palabras. ¡Añade algunas a tu Word Bank!',
        fr: 'Pas encore assez de mots. Ajoutez-en à votre Word Bank !',
        pt: 'Ainda não há palavras suficientes. Adicione algumas ao seu Word Bank!',
    },
    pickMeaning: { en: 'What does it mean?', it: 'Che cosa significa?', es: '¿Qué significa?', fr: 'Que signifie ce mot ?', pt: 'O que significa?' },
    pickWord: { en: 'Which word is it?', it: 'Qual è la parola?', es: '¿Cuál es la palabra?', fr: 'Quel est le mot ?', pt: 'Qual é a palavra?' },
    done: { en: 'Sprint complete!', it: 'Sprint completato!', es: '¡Sprint completado!', fr: 'Sprint terminé !', pt: 'Sprint concluído!' },
    bestStreak: { en: 'Best streak', it: 'Serie migliore', es: 'Mejor racha', fr: 'Meilleure série', pt: 'Melhor sequência' },
    review: { en: 'Review these', it: 'Da ripassare', es: 'Para repasar', fr: 'À revoir', pt: 'Para revisar' },
    again: { en: 'Play again →', it: 'Gioca ancora →', es: 'Jugar otra vez →', fr: 'Rejouer →', pt: 'Jogar de novo →' },
    back: { en: 'Back to Games', it: 'Torna ai giochi', es: 'Volver a los juegos', fr: 'Retour aux jeux', pt: 'Voltar aos jogos' },
    timeUp: { en: '⏱ Time\'s up!', it: '⏱ Tempo scaduto!', es: '⏱ ¡Se acabó el tiempo!', fr: '⏱ Temps écoulé !', pt: '⏱ O tempo acabou!' },
};

const TOTAL_ROUNDS = 15;
const ROUND_TENTHS = 50;

function shuffleList(list) {
    const copy = [...list];
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
}

const usable = (item) => item.word && item.translation && item.word.length <= 30 && item.translation.length <= 40;

// Builds one question: the word in the language being learned, or its meaning, with four possible answers
function buildQuestion(item, all) {
    const toWord = Math.random() < 0.4;
    const distractors = shuffleList(all.filter(w =>
        w.word !== item.word && w.translation.toLowerCase() !== item.translation.toLowerCase()
    )).slice(0, 3);
    return {
        item,
        toWord,
        prompt: toWord ? item.translation : item.word,
        options: shuffleList([item, ...distractors]),
    };
}

export default function TranslationSprint() {
    const navigate = useNavigate();
    const { activeLanguage, activeLevel } = useUserLanguage();
    const { interfaceLanguage } = useLanguage();
    const uiLang = interfaceLanguage || 'en';
    const tx = (key) => (TEXT[key] && (TEXT[key][uiLang] || TEXT[key].en)) || key;
    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();

    const [words, setWords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [round, setRound] = useState(0);
    const [question, setQuestion] = useState(null);
    const [selected, setSelected] = useState(null);
    const [tenths, setTenths] = useState(ROUND_TENTHS);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [missed, setMissed] = useState([]);
    const [finished, setFinished] = useState(false);
    const [earnedXp, setEarnedXp] = useState(0);
    const stats = useRef({ score: 0, best: 0 });
    const timerRef = useRef(null);
    const latest = useRef({});

    const start = (list) => {
        stats.current = { score: 0, best: 0 };
        setScore(0);
        setStreak(0);
        setBestStreak(0);
        setMissed([]);
        setFinished(false);
        setRound(0);
        setSelected(null);
        setTenths(ROUND_TENTHS);
        setQuestion(buildQuestion(list[0], list));
    };

    useEffect(() => {
        const init = async () => {
            const { data: { user } } = await supabase.auth.getUser();
            const level = activeLevel || localStorage.getItem('vozoly_level') || 'A1';
            const pool = await loadGamePool(user ? user.id : null, targetLang, interfaceLanguage, level);
            const seen = new Set();
            const list = pickGameWords(pool, 30, usable).filter(w => {
                const key = w.word.toLowerCase();
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
            setWords(list);
            if (list.length >= 5) {
                stats.current = { score: 0, best: 0 };
                setQuestion(buildQuestion(list[0], list));
            }
            setLoading(false);
        };
        init();
        return () => clearTimeout(timerRef.current);
    }, [targetLang, activeLevel, interfaceLanguage]);

    const totalRounds = Math.min(TOTAL_ROUNDS, words.length);

    const goNext = (fromRound) => {
        const next = fromRound + 1;
        if (next >= totalRounds) {
            // Two points per correct answer, plus a bonus for long streaks
            const xp = Math.min(30, stats.current.score * 2 + Math.floor(stats.current.best / 3) * 2);
            setEarnedXp(xp);
            setFinished(true);
            if (xp > 0) awardXP(xp, 'translation-sprint');
            return;
        }
        setRound(next);
        setSelected(null);
        setTenths(ROUND_TENTHS);
        setQuestion(buildQuestion(words[next], words));
    };

    const answer = (option) => {
        if (selected || !question) return;
        const key = option ? option.word : 'timeout';
        setSelected(key);
        if (option && option.word === question.item.word) {
            stats.current.score += 1;
            setScore(stats.current.score);
            setStreak(s => {
                const next = s + 1;
                if (next > stats.current.best) stats.current.best = next;
                setBestStreak(stats.current.best);
                return next;
            });
        } else {
            setStreak(0);
            setMissed(prev => [...prev, question.item]);
        }
        const fromRound = round;
        timerRef.current = setTimeout(() => goNext(fromRound), option && option.word === question.item.word ? 450 : 1000);
    };

    // Countdown in tenths of a second
    useEffect(() => {
        if (loading || finished || !question || selected) return undefined;
        if (tenths <= 0) {
            latest.current.timeout();
            return undefined;
        }
        const t = setTimeout(() => setTenths(n => n - 1), 100);
        return () => clearTimeout(t);
    }, [tenths, loading, finished, question, selected]);

    latest.current = { timeout: () => answer(null) };

    const backBar = (
        <div className="ts-top-bar">
            <button className="ts-back" onClick={() => navigate('/games')}>←</button>
            <div className="ts-title">{tx('title')}</div>
            <div style={{ width: 32 }}></div>
        </div>
    );

    if (loading) {
        return <div className="ts-screen">{backBar}<div className="ts-complete"><div className="ts-complete-stat">{tx('loading')}</div></div></div>;
    }

    if (words.length < 5 || !question) {
        return (
            <div className="ts-screen">
                {backBar}
                <div className="ts-complete">
                    <div className="ts-complete-stat">{tx('notEnough')}</div>
                    <button className="ts-back-btn" onClick={() => navigate('/games')}>{tx('back')}</button>
                </div>
            </div>
        );
    }

    if (finished) {
        return (
            <div className="ts-screen">
                {backBar}
                <div className="ts-complete">
                    <div className="ts-complete-icon">⚡</div>
                    <div className="ts-complete-title">{tx('done')}</div>
                    <div className="ts-complete-score">{score} / {totalRounds}</div>
                    <div className="ts-complete-stat">{tx('bestStreak')}: {bestStreak} 🔥</div>
                    <div className="ts-complete-xp">+{earnedXp} XP</div>
                    {missed.length > 0 && (
                        <div className="ts-review">
                            <div className="ts-review-label">{tx('review')}</div>
                            {missed.slice(0, 8).map((w, i) => (
                                <div key={i} className="ts-review-row">
                                    <span className="ts-review-word">{w.word}</span>
                                    <span className="ts-review-translation">{w.translation}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    <button className="ts-play-again" onClick={() => { const again = shuffleList(words); setWords(again); start(again); }}>{tx('again')}</button>
                    <button className="ts-back-btn" onClick={() => navigate('/games')}>{tx('back')}</button>
                </div>
            </div>
        );
    }

    const urgent = tenths <= 20 && !selected;

    return (
        <div className="ts-screen">
            <div className="ts-top-bar">
                <button className="ts-back" onClick={() => navigate('/games')}>←</button>
                <div className="ts-progress">{round + 1} / {totalRounds}</div>
                <div className="ts-streak">{streak > 1 ? `🔥 ${streak}` : ''}</div>
            </div>

            <div className="ts-timer-track">
                <div className={`ts-timer-fill ${urgent ? 'urgent' : ''}`} style={{ width: `${(tenths / ROUND_TENTHS) * 100}%` }}></div>
            </div>

            <div className="ts-question-label">{question.toWord ? tx('pickWord') : tx('pickMeaning')}</div>
            <div className="ts-prompt-card">
                <div className="ts-prompt">{question.prompt}</div>
            </div>

            <div className="ts-options">
                {question.options.map(opt => {
                    let cls = 'ts-option';
                    if (selected) {
                        if (opt.word === question.item.word) cls += ' correct';
                        else if (opt.word === selected) cls += ' wrong';
                        else cls += ' faded';
                    }
                    return (
                        <button key={opt.word} className={cls} onClick={() => answer(opt)} disabled={!!selected}>
                            {question.toWord ? opt.word : opt.translation}
                        </button>
                    );
                })}
            </div>

            {selected === 'timeout' && <div className="ts-timeout-msg">{tx('timeUp')}</div>}
        </div>
    );
}